import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchFactsByType } from "../api/factsApi";
import { Fact, FactApiEntity } from "../types";
import { formatDateTimeGMT5 } from "@/utils/timezone";
import {
  findScheduledFactByTitleAndType,
  getStoredScheduledFact,
} from "../utils/scheduledFactStorage";

export interface UseFactsTableOptions {
  type: string;
}

export interface UseFactsTableResult {
  facts: Fact[];
  rawFacts: FactApiEntity[];
  isLoading: boolean;
  queryKey: [string, string];
  refetch: () => Promise<unknown>;
  findFactById: (id: number) => FactApiEntity | undefined;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const formatDisplayDate = (yyyyMmDd: string): string => {
  const [year, month, day] = (yyyyMmDd || "").split("-");
  if (!year || !month || !day) return "";
  const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
  const dd = String(dateObj.getDate()).padStart(2, "0");
  const MMM = MONTH_LABELS[dateObj.getMonth()];
  const yyyy = dateObj.getFullYear();
  return `${dd}-${MMM}-${yyyy}`;
};

const extractDatePart = (input?: string): string | undefined => {
  if (!input) return undefined;
  const match = String(input).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : undefined;
};

export function useFactsTable({ type }: UseFactsTableOptions): UseFactsTableResult {
  const queryKey: [string, string] = ["facts", type];

  const { data = [], isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchFactsByType(type),
    placeholderData: [],
  });

  const formatDateTime = React.useCallback((input: string) => {
    if (!input) return "";
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return String(input);
    return formatDateTimeGMT5(d);
  }, []);

  const getScheduledDate = React.useCallback(
    (fact: FactApiEntity): { formatted: string; raw?: string } => {
      if (fact.id !== undefined && fact.id !== null) {
        const storedById = getStoredScheduledFact(fact.id);
        if (storedById?.scheduled_date) {
          const formatted = formatDisplayDate(storedById.scheduled_date);
          if (formatted) {
            return { formatted, raw: storedById.scheduled_date };
          }
        }
      }

      const storedByTitle = findScheduledFactByTitleAndType(String(fact.title ?? ""), fact.type ? String(fact.type) : undefined);
      if (storedByTitle?.scheduled_date) {
        const formatted = formatDisplayDate(storedByTitle.scheduled_date);
        if (formatted) {
          return { formatted, raw: storedByTitle.scheduled_date };
        }
      }

      const apiScheduledDate = extractDatePart(String(fact.scheduled_date ?? ""));
      const createdAt = extractDatePart(String((fact as any).created_at ?? (fact as any).createdAt ?? ""));

      const formatted = apiScheduledDate
        ? formatDisplayDate(apiScheduledDate)
        : createdAt
          ? formatDisplayDate(createdAt)
          : "Not scheduled";

      const fallbackRaw = apiScheduledDate ?? createdAt;

      return { formatted, raw: fallbackRaw };
    },
    []
  );

  const facts = React.useMemo<Fact[]>(() => {
    return data.map((fact) => {
      const scheduled = getScheduledDate(fact);
      return {
        id: Number(fact.id ?? 0),
        title: String(fact.title ?? ""),
        description: String(fact.description ?? ""),
        type: String(fact.type ?? type),
        is_tod: Boolean(fact.is_tod),
        created_at: formatDateTime(String((fact as any).created_at ?? (fact as any).createdAt ?? "")),
        scheduled_date: scheduled.formatted,
        scheduled_date_raw: scheduled.raw,
      };
    });
  }, [data, formatDateTime, getScheduledDate, type]);

  const findFactById = React.useCallback(
    (id: number) => data.find((fact) => Number(fact.id) === Number(id)),
    [data]
  );

  return {
    facts,
    rawFacts: data,
    isLoading,
    queryKey,
    refetch,
    findFactById,
  };
}
